import { BaseScene } from './BaseScene';
import { UserProfile, buyEarth, buyTower, buyPet, buyEquipment, saveUserProfile } from '../entities/base/UserProfile';
import { ALL_EQUIPMENT_CONFIGS, EquipmentType, getEquipmentsByType } from '../data/EquipmentData';
import { t } from '../i18n/lang';

const EARTH_SHOP = [
  { key: 'classic', name: 'Classic Earth', description: 'Balanced stats, good for beginners', cost: 0, currency: 'gold' },
  { key: 'ice', name: 'Ice Earth', description: 'High health, can freeze monsters', cost: 500, currency: 'gold', unlockLevel: 5 },
  { key: 'volcanic', name: 'Volcanic Earth', description: 'Reflects damage, many towers', cost: 800, currency: 'gold', unlockLevel: 10 },
  { key: 'crystal', name: 'Crystal Earth', description: 'High resistance, generates energy', cost: 1200, currency: 'gold', unlockLevel: 15 },
  { key: 'quantum', name: 'Quantum Earth', description: 'Can teleport towers', cost: 1500, currency: 'ruby', unlockLevel: 20 },
  { key: 'neutron', name: 'Neutron Earth', description: 'Very high health, slows monsters', cost: 2000, currency: 'ruby', unlockLevel: 25 },
];

const TOWER_SHOP = [
  { key: 'laser', name: 'Laser Tower', description: 'Balanced damage and speed', cost: 0, currency: 'gold' },
  { key: 'rapid', name: 'Rapid Fire Tower', description: 'High speed, low damage', cost: 200, currency: 'gold' },
  { key: 'heavy', name: 'Heavy Cannon', description: 'High damage, slow speed', cost: 300, currency: 'gold' },
  { key: 'missile', name: 'Missile Tower', description: 'Area damage, slow reload', cost: 400, currency: 'gold' },
  { key: 'freeze', name: 'Freeze Tower', description: 'Slows or freezes monsters', cost: 350, currency: 'gold' },
  { key: 'multi', name: 'Multi-Directional', description: 'Shoots in 4 directions', cost: 500, currency: 'gold', unlockLevel: 4 },
  { key: 'piercing', name: 'Piercing Tower', description: 'Pierces through 3 monsters', cost: 600, currency: 'gold', unlockLevel: 6 },
  { key: 'chain', name: 'Chain Lightning', description: 'Lightning chains to 3 targets', cost: 700, currency: 'gold', unlockLevel: 8 },
  { key: 'shotgun', name: 'Shotgun Tower', description: 'Fires spread of 5 projectiles', cost: 450, currency: 'gold', unlockLevel: 10 },
  { key: 'beam', name: 'Beam Tower', description: 'Continuous laser beam', cost: 800, currency: 'ruby', unlockLevel: 12 },
];

const PET_SHOP = [
  { key: 'laser', name: 'Laser Pet', description: 'Shoots laser beams at monsters', cost: 0, currency: 'gold' },
  { key: 'missile', name: 'Missile Pet', description: 'Fires homing missiles', cost: 300, currency: 'gold', unlockLevel: 10 },
  { key: 'drone', name: 'Drone Pet', description: 'Rapid-fire attacks with mobility', cost: 500, currency: 'gold', unlockLevel: 20 },
  { key: 'shield', name: 'Shield Pet', description: 'Protects Earth and reflects damage', cost: 700, currency: 'ruby', unlockLevel: 30 },
  { key: 'support', name: 'Support Pet', description: 'Buffs towers and Earth', cost: 1000, currency: 'ruby', unlockLevel: 40 },
];

type ShopTab = 'earth' | 'tower' | 'pet' | 'equipment';

export class ShopScene extends BaseScene {
  private userProfile: UserProfile;
  private currentTab: ShopTab = 'earth';
  private goldText: Phaser.GameObjects.Text;
  private rubyText: Phaser.GameObjects.Text;
  private contentContainer: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'Shop' });
  }

  override create(): void {
    super.create();
    this.userProfile = (window as any).userProfile;
    this.createHeader();
    this.createTabNavigation();
    this.createCurrencyDisplay();
    this.createContentArea();
    this.showTabContent(this.currentTab);
  }

  private createHeader(): void {
    const width = this.cameras.main.width;
    this.add.text(width / 2, 50, 'SHOP', {
      fontSize: '36px', fontFamily: 'Arial Black', color: '#FFD700', fontStyle: 'bold'
    }).setOrigin(0.5);
    // Back button
    const backBtn = this.add.text(60, 50, '← BACK', {
      fontSize: '18px', fontFamily: 'Arial Black', color: '#fff', backgroundColor: '#888', padding: { x: 12, y: 6 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => this.scene.start('MainMenu'));
    backBtn.on('pointerover', () => backBtn.setStyle({ backgroundColor: '#555' }));
    backBtn.on('pointerout', () => backBtn.setStyle({ backgroundColor: '#888' }));
  }

  private createTabNavigation(): void {
    const width = this.cameras.main.width;
    const tabY = 120;
    const tabs: { key: ShopTab, name: string, icon: string, color: number }[] = [
      { key: 'earth', name: 'EARTH', icon: '🌍', color: 0x27ae60 },
      { key: 'tower', name: 'TOWERS', icon: '🏗️', color: 0x3498db },
      { key: 'pet', name: 'PETS', icon: '🐾', color: 0x9b59b6 },
      { key: 'equipment', name: 'EQUIPMENT', icon: '⚔️', color: 0xf39c12 }
    ];
    const tabWidth = (width - 40) / tabs.length;
    tabs.forEach((tab, index) => {
      const tabX = 20 + (index * tabWidth) + (tabWidth / 2);
      const tabBtn = this.add.text(tabX, tabY, `${tab.icon} ${tab.name}`, {
        fontSize: '18px', fontFamily: 'Arial Black', color: this.currentTab === tab.key ? '#FFD700' : '#fff', backgroundColor: this.currentTab === tab.key ? '#444' : '#222', padding: { x: 18, y: 8 }
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      tabBtn.on('pointerdown', () => {
        this.currentTab = tab.key;
        this.showTabContent(tab.key);
        // Update tab style
        this.children.list.forEach((child: any) => {
          if (child.text && tabs.some(t => `${t.icon} ${t.name}` === child.text)) {
            child.setStyle({ color: child.text.startsWith(tab.icon) ? '#FFD700' : '#fff', backgroundColor: child.text.startsWith(tab.icon) ? '#444' : '#222' });
          }
        });
      });
    });
  }

  private createCurrencyDisplay(): void {
    const width = this.cameras.main.width;
    this.goldText = this.add.text(width - 220, 50, `GOLD: ${this.userProfile.gold}`, { fontSize: '18px', color: '#FFD700', fontFamily: 'Arial Black' });
    this.rubyText = this.add.text(width - 80, 50, `💎 ${this.userProfile.ruby}`, { fontSize: '18px', color: '#e74c3c', fontFamily: 'Arial Black' });
  }

  private createContentArea(): void {
    this.contentContainer = this.add.container(0, 160);
  }

  private showTabContent(tab: ShopTab): void {
    this.contentContainer.removeAll(true);
    switch (tab) {
      case 'earth':
        this.createEarthShop();
        break;
      case 'tower':
        this.createTowerShop();
        break;
      case 'pet':
        this.createPetShop();
        break;
      case 'equipment':
        this.createEquipmentShop();
        break;
    }
  }

  private createEarthShop(): void {
    EARTH_SHOP.forEach((item, idx) => {
      const y = 40 + idx * 80;
      const isOwned = this.userProfile.ownedEarths.includes(item.key);
      const canAfford = this.userProfile[item.currency as keyof UserProfile] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      this.createShopItem(item, y, isOwned, canAfford, canUnlock, 'earth');
    });
  }

  private createTowerShop(): void {
    TOWER_SHOP.forEach((item, idx) => {
      const y = 40 + idx * 80;
      const isOwned = this.userProfile.ownedTowers.includes(item.key);
      const canAfford = this.userProfile[item.currency as keyof UserProfile] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      this.createShopItem(item, y, isOwned, canAfford, canUnlock, 'tower');
    });
  }

  private createPetShop(): void {
    PET_SHOP.forEach((item, idx) => {
      const y = 40 + idx * 80;
      const isOwned = this.userProfile.ownedPets.includes(item.key);
      const canAfford = this.userProfile[item.currency as keyof UserProfile] >= item.cost;
      const canUnlock = !item.unlockLevel || this.userProfile.highestLevel >= item.unlockLevel;
      this.createShopItem(item, y, isOwned, canAfford, canUnlock, 'pet');
    });
  }

  private createEquipmentShop(): void {
    const types = [EquipmentType.WEAPON, EquipmentType.ARMOR, EquipmentType.ACCESSORY, EquipmentType.SPECIAL];
    let y = 0;
    types.forEach(type => {
      const configs = getEquipmentsByType(type);
      configs.forEach(config => {
        const canAfford = this.userProfile[config.currency as keyof UserProfile] >= config.cost;
        const canUnlock = !config.unlockLevel || this.userProfile.highestLevel >= config.unlockLevel;
        this.createEquipmentShopItem(config, y, canAfford, canUnlock);
        y += 60;
      });
      y += 20;
    });
  }

  private createShopItem(item: any, y: number, isOwned: boolean, canAfford: boolean, canUnlock: boolean, type: string): void {
    const width = this.cameras.main.width;
    const bg = this.add.rectangle(width / 2, y, 700, 70, isOwned ? 0x444444 : 0x222244, 0.8);
    this.contentContainer.add(bg);
    const nameText = this.add.text(width / 2 - 300, y - 15, item.name, { fontSize: '18px', color: '#FFD700', fontFamily: 'Arial Black' }).setOrigin(0, 0.5);
    this.contentContainer.add(nameText);
    const descText = this.add.text(width / 2 - 300, y + 15, item.description, { fontSize: '13px', color: '#cccccc', fontFamily: 'Arial' }).setOrigin(0, 0.5);
    this.contentContainer.add(descText);
    const costText = this.add.text(width / 2 + 120, y, `${item.cost} ${item.currency === 'gold' ? 'GOLD' : '💎'}`, { fontSize: '16px', color: item.currency === 'gold' ? '#FFD700' : '#e74c3c', fontFamily: 'Arial Black' }).setOrigin(0.5);
    this.contentContainer.add(costText);
    const buyBtn = this.add.text(width / 2 + 260, y, isOwned ? 'OWNED' : (canUnlock ? 'BUY' : 'LOCKED'), {
      fontSize: '16px', color: '#fff', backgroundColor: isOwned ? '#888' : (canUnlock ? (canAfford ? '#4CAF50' : '#888') : '#555'), padding: { x: 18, y: 8 }, fontFamily: 'Arial Black'
    }).setOrigin(0.5);
    if (!isOwned && canUnlock && canAfford) {
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerdown', () => {
        let success = false;
        if (type === 'earth') success = buyEarth(this.userProfile, item.key, item.cost);
        if (type === 'tower') success = buyTower(this.userProfile, item.key, item.cost);
        if (type === 'pet') success = buyPet(this.userProfile, item.key, item.cost);
        if (success) {
          (window as any).userProfile = this.userProfile;
          this.updateCurrencyDisplay();
          this.showTabContent(type as ShopTab);
        }
      });
      buyBtn.on('pointerover', () => buyBtn.setStyle({ backgroundColor: '#388E3C' }));
      buyBtn.on('pointerout', () => buyBtn.setStyle({ backgroundColor: '#4CAF50' }));
    }
    this.contentContainer.add(buyBtn);
  }

  private createEquipmentShopItem(config: any, y: number, canAfford: boolean, canUnlock: boolean): void {
    const width = this.cameras.main.width;
    const bg = this.add.rectangle(width / 2, y, 700, 50, 0x222244, 0.8);
    this.contentContainer.add(bg);
    const nameText = this.add.text(width / 2 - 300, y - 10, config.displayName, { fontSize: '15px', color: '#FFD700', fontFamily: 'Arial Black' }).setOrigin(0, 0.5);
    this.contentContainer.add(nameText);
    const descText = this.add.text(width / 2 - 300, y + 10, config.description, { fontSize: '12px', color: '#cccccc', fontFamily: 'Arial' }).setOrigin(0, 0.5);
    this.contentContainer.add(descText);
    const costText = this.add.text(width / 2 + 120, y, `${config.cost} ${config.currency === 'gold' ? 'GOLD' : '💎'}`, { fontSize: '14px', color: config.currency === 'gold' ? '#FFD700' : '#e74c3c', fontFamily: 'Arial Black' }).setOrigin(0.5);
    this.contentContainer.add(costText);
    const buyBtn = this.add.text(width / 2 + 260, y, canUnlock ? (canAfford ? 'BUY' : 'NO $') : 'LOCKED', {
      fontSize: '14px', color: '#fff', backgroundColor: canUnlock ? (canAfford ? '#4CAF50' : '#888') : '#555', padding: { x: 14, y: 6 }, fontFamily: 'Arial Black'
    }).setOrigin(0.5);
    if (canUnlock && canAfford) {
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerdown', () => {
        const success = buyEquipment(this.userProfile, config.key, config.cost, config.currency as 'gold' | 'ruby');
        if (success) {
          (window as any).userProfile = this.userProfile;
          this.updateCurrencyDisplay();
          this.showTabContent('equipment');
        }
      });
      buyBtn.on('pointerover', () => buyBtn.setStyle({ backgroundColor: '#388E3C' }));
      buyBtn.on('pointerout', () => buyBtn.setStyle({ backgroundColor: '#4CAF50' }));
    }
    this.contentContainer.add(buyBtn);
  }

  private updateCurrencyDisplay(): void {
    this.goldText.setText(`GOLD: ${this.userProfile.gold}`);
    this.rubyText.setText(`💎 ${this.userProfile.ruby}`);
  }
} 